/* eslint-disable complexity */
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CitationsSection } from '~/components/citations-section/CitationsSection';
import { ImpactedCompanies } from '~/modules/impacted-companies/ImpactedCompanies';
import { ThematicFocus } from '~/modules/thematic-focus/ThematicFocus';
import { useEntity } from '~/react-query-hooks/entities/useEntities';
import { useEarningsInsightsFilter } from '~/react-query-hooks/insights/useEarningsInsightsFilter';
import { useGetEarningsInsightPreview } from '~/react-query-hooks/insights/useGetEarningsInsightPreview';

import { TextSection } from '../../../../components/text-section/TextSection';
import { useEarningsInsightDrawer } from '../../useEarningsInsightDrawer';
import { BullBearPoints } from '../components/bull-bear-points/BullBearPoints';
import { Sidebar } from '../components/side-bar/Sidebar';
import { useSidebarItems } from '../components/side-bar/useSidebarItems';
import * as Shared from '../EarningsInsightWidget.styles';
import { EpsExpectationsSection } from './components/eps-expectations-section/EpsExpectationsSection';
import { KpisTable } from './components/kpis-table/KpisTable';
import { PreviewOverview } from './components/PreviewOverview';
import * as S from './EarningsPreview.styles';

export const EarningsPreview = () => {
  const { t } = useTranslation(['earnings']);
  const containerRef = useRef<HTMLDivElement>(null);
  const { activeEntityTag, earningsId } = useEarningsInsightDrawer();

  const {
    data: earningsInsightsData,
    isFetching: isEarningsInsightsFetching,
    isError: isEarningsInsightsError,
    error: earningsInsightsError,
  } = useEarningsInsightsFilter({
    body: {
      earnings_ids: [earningsId ?? ''],
    },
    isEnabled: !!earningsId,
  });

  const {
    data: entityData,
    isFetching,
    isError: isEntityError,
    error,
  } = useEntity(activeEntityTag ?? '');
  const { previewItems } = useSidebarItems();

  const {
    data: previewData,
    isFetching: isPreviewFetching,
    isError: isPreviewError,
    error: previewError,
  } = useGetEarningsInsightPreview(
    earningsInsightsData?.find(insight => insight.type === 'preview')?.id ?? ''
  );

  const isLoading =
    isFetching || isPreviewFetching || isEarningsInsightsFetching;

  const isError = isPreviewError || isEarningsInsightsError || isEntityError;

  if (isLoading) {
    return <Shared.StyledLoader />;
  }

  if (isError || !entityData?.data) {
    throw new Error(
      error?.message || previewError?.message || earningsInsightsError?.message
    );
  }

  if (!previewData) {
    return (
      <Shared.Container>
        <Shared.EmptyWrapper>
          <Shared.StyledImg
            alt=""
            src="/app/static/images/empty-state/radio-telescope.png"
            height={130}
            width={146}
          />
          <Shared.EmptyTitle>
            {t('earnings:earningsInsight.drawer.earningsPreview.emptyTitle')}
          </Shared.EmptyTitle>
        </Shared.EmptyWrapper>
      </Shared.Container>
    );
  }

  const entity = entityData.data;

  return (
    <S.Wrapper>
      <S.Separator>
        <S.LeftContainer>
          <S.ScrollableContainer ref={containerRef}>
            <S.Section ref={previewItems[0].ref} data-testid="preview-overview">
              <PreviewOverview
                title={previewData.title}
                activeEntityTag={activeEntityTag ?? ''}
              />
            </S.Section>
            <S.Section
              ref={previewItems[1].ref}
              data-testid="earnings-reaction-scenario"
            >
              {earningsId && (
                <EpsExpectationsSection
                  earningsId={earningsId}
                  entity={entity}
                />
              )}
              <S.TextSectionWrapper>
                <TextSection
                  useBulletPoints
                  variant="secondary"
                  title={t(
                    'earningsInsight.drawer.earningsPreview.sentimentAndExpectationsSetup'
                  )}
                  textContent={previewData.historical_context}
                />
                <TextSection
                  useBulletPoints
                  variant="secondary"
                  title={t(
                    'earningsInsight.drawer.earningsPreview.seasonality'
                  )}
                  textContent={previewData.seasonality}
                />
              </S.TextSectionWrapper>
            </S.Section>
            <S.Section ref={previewItems[2].ref} data-testid="kpis">
              <KpisTable kpisData={previewData.kpis} />
            </S.Section>
            <S.Section ref={previewItems[3].ref} data-testid="thematic-focus">
              <ThematicFocus
                themes={previewData.themes}
                otherThemes={previewData.other_themes}
                title={t(
                  'earningsInsight.drawer.earningsPreview.items.thematicFocusPreview'
                )}
              />
            </S.Section>
            <S.Section ref={previewItems[4].ref} data-testid="key-items">
              <TextSection
                useBulletPoints
                variant="primary"
                title={t(
                  'earningsInsight.drawer.earningsPreview.items.keyItems'
                )}
                textContent={previewData.key_items}
              />
            </S.Section>
            <S.Section
              ref={previewItems[5].ref}
              data-testid="investment-outlook"
            >
              <BullBearPoints
                data={previewData.investment_outlook}
                title={t(
                  'earningsInsight.drawer.earningsPreview.items.investmentOutlook'
                )}
              />
            </S.Section>
            <S.Section
              ref={previewItems[6].ref}
              data-testid="impacted-companies"
            >
              <ImpactedCompanies
                impactedCompanies={previewData.impacted_companies}
                title={t('earnings:earningsInsight.drawer.impactedCompanies')}
                insightCreatedAt={previewData.created_at}
              />
            </S.Section>
            <S.Section data-testid="citations">
              {previewData?.citations?.length > 0 && (
                <CitationsSection
                  title={t('earningsInsight.drawer.webLinks')}
                  count={previewData.citations.length ?? 0}
                  items={previewData.citations ?? []}
                />
              )}
            </S.Section>
          </S.ScrollableContainer>
        </S.LeftContainer>
        <Sidebar containerRef={containerRef} items={previewItems} />
      </S.Separator>
    </S.Wrapper>
  );
};
